import { ApolloCache, useQuery, useMutation } from '@apollo/client';
import { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { CreditCard, AlertCircle, Lock, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  GET_USER_ACCOUNTS,
  GET_ACCOUNT_BILLING_DETAILS,
  GET_ACCOUNT_DETAILS,
  GET_USER_PROFILE,
} from '../../graphql';
import {
  BILLING_PORTAL_URL,
  CREATE_SETUP_INTENT,
  ADD_PAYMENT_METHOD,
  REMOVE_PAYMENT_METHOD,
  MARK_PAYMENT_METHOD_DEFAULT,
  UPDATE_PRODUCT_PLAN,
  CANCEL_SUBSCRIPTION,
} from '../../graphql/mutations';
import {
  GetUserAccountsResponse,
  GetAccountBillingDetailsResponse,
  GetAccountDetailsResponse,
} from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { useProduct } from '../../context/ProductContext';
import { useTheme } from '../../context/ThemeContext';
import {
  BillingInterval,
  money,
  planPrice,
  toInterval,
  useTinycmsPlans,
} from '../account/tinycmsPlans';
import { TinycmsChangePlan } from './TinycmsChangePlan';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader } from '../common/PageLoader';
import { BillingContactSection } from './BillingContact';
import { useBillingContact } from './siteOwner';
import { showInkToast } from '../cms/InkToast';

type CardList = GetAccountBillingDetailsResponse['billingDetails'];

const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

interface UserProfileData {
  me?: {
    id: string;
    email: string;
  };
}

// Plan configuration mapping
const PLAN_CONFIG: Record<string, { price: string; users: string }> = {
  Enterprise: { price: '$149.00', users: '10' },
  Pro: { price: '$29.00', users: '3' },
  Basic: { price: '$9.00', users: '1' },
};

function pricePerMonth(value: number | string | undefined) {
  if (value == null || value === '') return '—';
  const amount = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(amount)) return '—';
  const shown = Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
  return `${shown} / mo`;
}

// Pricing plans (matching SignUp.tsx)
interface PricingPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  popular?: boolean;
}

const pricingPlans: PricingPlan[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 9,
    description: 'Perfect for individuals',
    features: [
      'Up to 2 social profiles',
      'Up to 100 posts per month',
      'Basic analytics',
      'Email support',
      '14-day free trial',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    description: 'For professionals and small teams',
    features: [
      'Unlimited 10 social profiles',
      'Up to 1000 posts per month',
      'Advanced analytics',
      'Priority support',
      'Team collaboration',
    ],
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 149,
    description: 'For large organizations',
    features: [
      'Everything in Pro',
      'Unlimited social profiles',
      'Unlimited posts per month',
      'Manage multiple organizations',
      'SSO integration',
      'Custom 3rd party integrations',
      'Dedicated account manager',
      'SLA guarantee',
    ],
  },
];

// Stripe draws the card field in its own iframe, so it can't see our CSS
// variables. Read the Compositor tokens the app's <Input> uses (text-cmp-ink,
// placeholder:text-cmp-ink-3) and hand Stripe the resolved colours.
function cardElementOptions() {
  const css = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  return {
    style: {
      base: {
        fontSize: '16px',
        color: v('--cmp-ink', '#17140f'),
        iconColor: v('--cmp-ink-3', '#857e70'),
        '::placeholder': {
          color: v('--cmp-ink-3', '#857e70'),
        },
        fontFamily: 'system-ui, sans-serif',
      },
      invalid: {
        color: v('--cmp-crit', '#9c4a3c'),
        iconColor: v('--cmp-crit', '#9c4a3c'),
      },
    },
    hidePostalCode: true,
  };
}

interface SetupIntentResponse {
  setupIntent: {
    clientSecret: string;
  };
}

interface AddPaymentMethodResponse {
  updatePaymentMethod: {
    cardLast4: string;
    cardExpMonth: number;
    cardExpYear: number;
    isDefault: boolean;
    isRemovable: boolean;
  };
}

interface PaymentFormProps {
  accountId: string;
  userEmail?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function PaymentForm({ accountId, userEmail, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [errors, setErrors] = useState<{ card?: string; submit?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoadingSetupIntent, setIsLoadingSetupIntent] = useState<boolean>(true);
  // Recompute when the appearance toggle flips; CardElement passes new options
  // to the mounted field. `theme` is the trigger, not an input: the tokens it
  // swaps live on <html>.
  const { theme } = useTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cardOptions = useMemo(() => cardElementOptions(), [theme]);

  const [createSetupIntent] = useMutation<SetupIntentResponse>(CREATE_SETUP_INTENT);
  const [addPaymentMethod] = useMutation<AddPaymentMethodResponse>(ADD_PAYMENT_METHOD);

  useEffect(() => {
    const fetchSetupIntent = async () => {
      try {
        const { data } = await createSetupIntent();
        if (data?.setupIntent?.clientSecret) {
          setClientSecret(data.setupIntent.clientSecret);
        }
      } catch (error) {
        console.error('Failed to create setup intent:', error);
        setErrors({ submit: 'Failed to initialize payment form. Please try again.' });
      } finally {
        setIsLoadingSetupIntent(false);
      }
    };

    fetchSetupIntent();
  }, [createSetupIntent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement || !clientSecret) {
        setErrors({ submit: 'Payment form not ready. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: userEmail
            ? {
                email: userEmail,
              }
            : undefined,
        },
      });

      if (stripeError) {
        setErrors({ submit: stripeError.message || 'Payment setup failed. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        const { data } = await addPaymentMethod({
          variables: {
            accountId: accountId,
            paymentMethod: setupIntent.payment_method as string,
          },
        });

        if (data?.updatePaymentMethod) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Add payment method failed:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add payment method. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingSetupIntent) {
    return (
      <div className="py-8">
        <Loader label="Loading payment form" className="text-center text-muted-foreground" />
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="py-8">
        <p className="text-center text-destructive">
          {errors.submit || 'Failed to initialize payment form. Please try again.'}
        </p>
      </div>
    );
  }

  const isTestMode =
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_') ||
    !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        {/* Test Mode Banner */}
        {isTestMode && (
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-500">Test Mode</p>
              <p className="text-xs text-yellow-400/80 mt-1">
                Use test card: 4242 4242 4242 4242, any future expiry date, and any 3-digit CVC
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="card-element">Card Details</Label>
          <div className="p-3 bg-form-dark rounded border border-cmp-line-2">
            <CardElement id="card-element" options={cardOptions} />
          </div>
          {errors.card && <p className="text-destructive text-sm">{errors.card}</p>}
        </div>

        <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
          <Lock className="w-4 h-4 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            Your payment information is encrypted and secure
          </p>
        </div>
        {errors.submit && <p className="text-destructive text-sm">{errors.submit}</p>}
      </div>
      <DialogFooter className="mt-6">
        <Button variant="outline" type="button" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || !stripe}
          className="bg-button-primary text-button-primary"
        >
          {isSubmitting ? 'Saving...' : 'Save Payment Method'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function BillingSettings() {
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);
  const [isCancelSubscriptionOpen, setIsCancelSubscriptionOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  // tinycms shell: the Free / Personal / Business / Pro plans, monthly or yearly.
  const { product } = useProduct();
  const isTinycms = product === 'atomizer';
  const { plans: tinycmsPlans } = useTinycmsPlans();
  const [planInterval, setPlanInterval] = useState<BillingInterval>('year');
  const [planError, setPlanError] = useState('');

  const {
    accountId,
    selectedAccount: organization,
    isLoading: loading,
    error,
    refetchAccounts,
  } = useAccount();
  const [removePaymentMethod] = useMutation<{ deletePaymentMethod: CardList }>(
    REMOVE_PAYMENT_METHOD
  );
  const [markPaymentMethodDefault] = useMutation<{ setDefaultPaymentMethod: CardList }>(
    MARK_PAYMENT_METHOD_DEFAULT
  );
  const [updateProductPlan, { loading: isUpdatingPlan }] = useMutation(UPDATE_PRODUCT_PLAN);
  const [cancelSubscription, { loading: isCancelingSubscription }] =
    useMutation(CANCEL_SUBSCRIPTION);

  // Get account details including activeOrder for plan, monthly price, and next billing date
  const {
    loading: accountDetailsLoading,
    data: accountDetailsData,
    refetch: refetchAccountDetails,
  } = useQuery<GetAccountDetailsResponse>(GET_ACCOUNT_DETAILS, {
    variables: { id: accountId },
    skip: !accountId,
    onError: (error) => {
      console.error('Account Details Query Error:', error);
    },
  });

  const { data: billingData, refetch: refetchBilling } = useQuery<GetAccountBillingDetailsResponse>(
    GET_ACCOUNT_BILLING_DETAILS,
    {
      variables: { accountId: accountId },
      skip: !accountId,
      onError: (error) => {
        console.error('Billing Details Query Error:', error);
      },
    }
  );

  // Get user email for billing details
  const { data: userProfileData } = useQuery<UserProfileData>(GET_USER_PROFILE);

  const [openPortal, { loading: openingPortal }] = useMutation<{ billingPortalUrl: string }>(
    BILLING_PORTAL_URL
  );
  const [portalError, setPortalError] = useState('');
  // The site owner manages billing (any admin while there's no owner).
  const { contact, refetch: refetchContact } = useBillingContact(accountId);
  const canManage = contact ? contact.canManage : true;
  // Stripe's page opens in a new tab (App Prototype, Billing). The tab opens
  // on the click, before the address comes back, so it isn't blocked as a popup.
  const goToPortal = async () => {
    setPortalError('');
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const { data } = await openPortal({ variables: { accountId } });
      if (!data?.billingPortalUrl) throw new Error('That didn’t open. Try again.');
      if (tab) tab.location.href = data.billingPortalUrl;
      else window.location.assign(data.billingPortalUrl);
    } catch (err) {
      tab?.close();
      setPortalError(err instanceof Error ? err.message : 'That didn’t open. Try again.');
    }
  };

  // Get plan, monthly price, and next billing date from account details
  const accountDetails = accountDetailsData?.account;
  const currentPlan = accountDetails?.plan || organization?.plan || '';
  const activeOrder = accountDetails?.activeOrder;

  const monthlyPrice = activeOrder?.monthlyRate
    ? pricePerMonth(activeOrder.monthlyRate)
    : pricePerMonth(PLAN_CONFIG[currentPlan]?.price);

  // The Stripe subscription, as last synced (status "grandfathered": a paid
  // plan from before billing, not charged).
  const billing = accountDetails?.billing || null;
  const billingStatus = billing?.status || 'none';
  const isTrialing = billingStatus === 'trialing';
  const isPastDue = billingStatus === 'past_due';
  const isGrandfathered = billingStatus === 'grandfathered';
  const isComplimentary = billingStatus === 'complimentary';
  const isComped = isGrandfathered || isComplimentary;
  const giftUntil = isComplimentary ? billing?.complimentaryUntil || null : null;
  // Check if subscription is cancelled (ends at the period's end)
  const isCancelled = Boolean(billing?.cancelAtPeriodEnd) || !!activeOrder?.orderCancelDate;

  // Format date helper
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return date.getFullYear() === new Date().getFullYear() ? day : `${day}, ${date.getFullYear()}`;
  };

  // Calculate active until date (one day before orderNextBillDate)
  const getActiveUntilDate = () => {
    if (!activeOrder?.orderNextBillDate) return '—';
    const nextBillDate = new Date(activeOrder.orderNextBillDate);
    const activeUntil = new Date(nextBillDate);
    activeUntil.setDate(activeUntil.getDate() - 1);
    return formatDate(activeUntil.toISOString());
  };

  const periodEndDate = billing?.periodEnd || activeOrder?.orderNextBillDate;
  const nextBillingDate = isCancelled
    ? formatDate(billing?.periodEnd || activeOrder?.orderCancelDate)
    : formatDate(periodEndDate);

  const activeUntilDate = isCancelled
    ? billing?.periodEnd
      ? formatDate(billing.periodEnd)
      : getActiveUntilDate()
    : null;

  const tinycmsPlan = isTinycms
    ? tinycmsPlans.find((p) => p.key === (activeOrder?.plan || currentPlan).toLowerCase())
    : undefined;
  const orderInterval = toInterval(activeOrder?.billingInterval || 'month');
  const planLabel = tinycmsPlan?.label || currentPlan;
  const tinycmsPrice = tinycmsPlan ? planPrice(tinycmsPlan, orderInterval) : 0;
  const priceLabel = tinycmsPlan
    ? tinycmsPrice
      ? `${money(tinycmsPrice)} / mo`
      : 'Free'
    : monthlyPrice;
  // Under the price: how it's billed ("$216 billed yearly").
  const billingCycle =
    tinycmsPlan && tinycmsPrice
      ? orderInterval === 'year'
        ? `${money(tinycmsPrice * 12)} billed yearly`
        : 'Billed monthly'
      : '';
  // A Free plan has no bill to show or subscription to cancel.
  const isFreePlan = Boolean(tinycmsPlan) && !Number(activeOrder?.monthlyRate);
  const planConfig = tinycmsPlan
    ? { price: priceLabel, users: String(tinycmsPlan.maxMembers) }
    : PLAN_CONFIG[currentPlan] || { price: monthlyPrice, users: 'N/A' };

  const handleAddPaymentMethodSuccess = () => {
    setIsAddPaymentMethodOpen(false);
    // Refetch billing details to show the new payment method
    refetchBilling();
  };

  // Both card mutations answer with the customer's whole card list, so the
  // screen takes it as given: Default and Remove follow the server at once,
  // with no refresh (backlog a3b1f198).
  const keepCards = (cache: ApolloCache<unknown>, list?: CardList | null) => {
    if (!list) return;
    cache.writeQuery({
      query: GET_ACCOUNT_BILLING_DETAILS,
      variables: { accountId },
      data: { billingDetails: list },
    });
  };
  const [cardError, setCardError] = useState('');
  // The card whose Remove is waiting on "Remove card" (only the last card asks).
  const [confirmingCard, setConfirmingCard] = useState<string | null>(null);
  const [busyCard, setBusyCard] = useState<string | null>(null);

  const handleRemovePaymentMethod = async (card: CardList[number]) => {
    if (!accountId) return;
    setCardError('');
    setBusyCard(card.id);
    try {
      await removePaymentMethod({
        variables: { accountId, paymentMethod: card.id },
        update: (cache, { data }) => keepCards(cache, data?.deletePaymentMethod),
      });
      setConfirmingCard(null);
      showInkToast(`•••• ${card.cardLast4} was removed.`);
    } catch (error) {
      setCardError(error instanceof Error ? error.message : 'That didn’t work. Try again.');
      refetchBilling();
    } finally {
      setBusyCard(null);
    }
  };

  const handleSetDefaultPaymentMethod = async (card: CardList[number]) => {
    if (!accountId) return;
    setCardError('');
    setBusyCard(card.id);
    try {
      await markPaymentMethodDefault({
        variables: { accountId, paymentMethod: card.id },
        update: (cache, { data }) => keepCards(cache, data?.setDefaultPaymentMethod),
      });
      showInkToast(`Renewals charge •••• ${card.cardLast4} now.`);
    } catch (error) {
      setCardError(error instanceof Error ? error.message : 'That didn’t work. Try again.');
      refetchBilling();
    } finally {
      setBusyCard(null);
    }
  };

  const handleOpenChangePlan = () => {
    setPlanError('');
    if (isTinycms) {
      setSelectedPlan(tinycmsPlan?.key || 'personal');
      setPlanInterval(tinycmsPlan ? orderInterval : 'year');
      setIsChangePlanOpen(true);
      return;
    }
    // Set the current plan as selected when opening the modal
    // Convert current plan (e.g., "Pro", "Basic") to plan ID (e.g., "pro", "basic")
    const currentPlanId = currentPlan.toLowerCase();
    // Check if the plan ID exists in pricingPlans, otherwise default to 'basic'
    const planExists = pricingPlans.some((plan) => plan.id === currentPlanId);
    setSelectedPlan(planExists ? currentPlanId : 'basic');
    setIsChangePlanOpen(true);
  };

  /** tinycms: the plan and billing chosen in Change plan, applied now. */
  const handleSwitchTinycmsPlan = async (planKey: string, interval: BillingInterval) => {
    if (!accountId) return;
    setPlanError('');
    try {
      await updateProductPlan({
        variables: {
          accountId,
          productPlan: planKey,
          billingInterval: interval,
          product: 'tinycms',
        },
      });
      setIsChangePlanOpen(false);
      // The plan can change the account's products (Pro adds Atomizer and Reach).
      refetchAccounts();
      refetchAccountDetails();
    } catch (err) {
      setPlanError(err instanceof Error ? err.message : 'Your plan couldn’t be changed.');
    }
  };

  const handleSavePlan = async () => {
    if (!accountId || !selectedPlan) {
      console.error('Account ID and selected plan are required');
      return;
    }
    if (isTinycms) {
      setPlanError('');
      try {
        await updateProductPlan({
          variables: {
            accountId,
            productPlan: selectedPlan,
            billingInterval: planInterval,
            product: 'tinycms',
          },
        });
        setIsChangePlanOpen(false);
        // The plan can change the account's products (Pro adds Atomizer and Reach).
        refetchAccounts();
        refetchAccountDetails();
      } catch (err) {
        setPlanError(err instanceof Error ? err.message : 'Could not change the plan');
      }
      return;
    }

    try {
      // Find the plan details to get the proper name
      const planDetails = pricingPlans.find((plan) => plan.id === selectedPlan);
      if (!planDetails) {
        console.error('Selected plan not found');
        return;
      }

      // Use the plan name from pricingPlans (e.g., "Pro", "Basic", "Enterprise")
      const planName = planDetails.name;

      await updateProductPlan({
        variables: {
          accountId: accountId,
          productPlan: planName,
        },
        update: (cache) => {
          // Update the user accounts cache with the new plan
          const existingData = cache.readQuery<GetUserAccountsResponse>({
            query: GET_USER_ACCOUNTS,
          });

          if (existingData?.userAccounts) {
            const updatedAccounts = existingData.userAccounts.map((account) =>
              account.id === accountId ? { ...account, plan: planName } : account
            );

            cache.writeQuery({
              query: GET_USER_ACCOUNTS,
              data: {
                userAccounts: updatedAccounts,
              },
            });
          }
        },
      });

      setIsChangePlanOpen(false);
      // Refetch accounts and account details to ensure UI is in sync
      refetchAccounts();
      refetchAccountDetails();
    } catch (error) {
      console.error('Failed to update plan:', error);
      // You might want to show an error toast/notification here
    }
  };

  const handleCancelSubscription = async () => {
    if (!accountId) {
      console.error('Account ID is required to cancel subscription');
      return;
    }

    try {
      // The site stays active: the plan runs to the end of the paid period.
      await cancelSubscription({ variables: { accountId } });

      setIsCancelSubscriptionOpen(false);
      // Refetch accounts and account details to ensure UI is in sync
      refetchAccounts();
      refetchAccountDetails();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      // You might want to show an error toast/notification here
    }
  };

  const seatCap = Number(planConfig.users);
  const seatsUsed = accountDetails?.users?.length ?? 0;
  const seats = Number.isFinite(seatCap) && seatCap > 0 ? `${seatsUsed} of ${seatCap}` : '—';
  const cards = billingData?.billingDetails || [];

  // The plan tiles in grey bars while they load (QA round 7: a lone loader
  // read as an empty page for seconds).
  if (loading || accountDetailsLoading) return <BillingSkeleton />;
  if (error || !organization) return <p className="ink-lede">Could not load billing.</p>;

  return (
    <div>
      <h1 className="ink-h1">Billing</h1>
      <p className="ink-lede">
        Your billing details including plan, payment details, and invoices.
      </p>
      <div className="ink-bill">
        <div>
          <div className="ink-bill-label">Plan</div>
          <div className="ink-bill-value">{planLabel || '—'}</div>
        </div>
        <div>
          <div className="ink-bill-label">Price</div>
          <div className="ink-bill-value">{priceLabel}</div>
          {giftUntil ? (
            <div className="ink-bill-sub">No charge until {formatDate(giftUntil)}</div>
          ) : billingCycle ? (
            <div className="ink-bill-sub">{billingCycle}</div>
          ) : null}
        </div>
        <div>
          <div className="ink-bill-label">Seats</div>
          <div className="ink-bill-value">{seats}</div>
        </div>
        <div>
          <div className="ink-bill-label">
            {giftUntil
              ? 'Complimentary until'
              : isFreePlan
                ? 'Next bill'
                : isCancelled
                  ? 'Moves to Free'
                  : isTrialing
                    ? 'Trial ends'
                    : 'Next bill'}
          </div>
          <div className="ink-bill-value">
            {giftUntil
              ? formatDate(giftUntil)
              : isFreePlan
                ? 'No charge'
                : isComped
                  ? 'Not billed'
                  : isCancelled && activeUntilDate
                    ? activeUntilDate
                    : isTrialing
                      ? formatDate(billing?.trialEnd || undefined)
                      : nextBillingDate}
          </div>
        </div>
        {/* What the plan's state means, inside the card (Prototype). */}
        {isPastDue ? (
          <p className="ink-bill-note" data-tone="warn">
            Your last payment didn’t go through. Stripe will try again over the next few days;
            update your card below to keep {planLabel}. If it still fails, your site moves to Free
            (it stays online).
          </p>
        ) : isTrialing && !isCancelled ? (
          <p className="ink-bill-note">
            Your free trial ends on {formatDate(billing?.trialEnd || undefined)}. Your card is
            charged then{billingCycle ? ` (${billingCycle.toLowerCase()})` : ''} unless you move to
            Free first.
          </p>
        ) : isComplimentary && giftUntil ? (
          <p className="ink-bill-note">
            Your {planLabel} plan is complimentary until {formatDate(giftUntil)}. There’s no charge
            until then. To keep {planLabel}, add a card and choose it before that date. After that,
            this site moves to Free.
          </p>
        ) : isComplimentary ? (
          <p className="ink-bill-note">
            Your {planLabel} plan is complimentary — no credit card — for as long as you remain an
            active tinycms user. Choosing a different paid plan starts billing.
          </p>
        ) : isGrandfathered ? (
          // Already on a paid plan, so not "choosing a paid plan" (backlog b46da16c).
          <p className="ink-bill-note">
            Your plan started before billing did, so you haven’t been charged, and nothing changes
            on your site. Billing starts only if you move to a different plan.
          </p>
        ) : null}
      </div>
      <div className="ink-bill-actions" style={canManage ? undefined : { display: 'none' }}>
        <button type="button" className="ink-btn" onClick={handleOpenChangePlan}>
          {isCancelled ? 'Keep my plan' : 'Change plan'}
        </button>
        {!isCancelled && !isFreePlan ? (
          <button
            type="button"
            className="ink-bill-cancel"
            onClick={() => setIsCancelSubscriptionOpen(true)}
          >
            Cancel subscription
          </button>
        ) : null}
      </div>

      {accountId ? (
        <BillingContactSection
          accountId={accountId}
          contact={contact}
          onChanged={() => void refetchContact()}
        />
      ) : null}

      {/* Payment method (App Prototype, Billing): Add card on the heading row. */}
      <div className="ink-conn-head">
        <h2>Payment method</h2>
        {cards.length > 0 && canManage ? (
          <button type="button" className="ink-btn" onClick={() => setIsAddPaymentMethodOpen(true)}>
            Add card
          </button>
        ) : null}
      </div>
      {cards.length === 0 ? (
        <div className="ink-bill-pay" data-card="true">
          <span style={{ font: '400 14px/1.4 var(--ink-font)' }}>
            <span style={{ fontWeight: 500 }}>No card on file</span>
            {isFreePlan ? null : (
              <span style={{ color: 'var(--ink-ink3)' }}> · add one before your next bill</span>
            )}
          </span>
          {canManage ? (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => setIsAddPaymentMethodOpen(true)}
            >
              Add card
            </button>
          ) : null}
        </div>
      ) : (
        cards.map((detail) => {
          const busy = busyCard === detail.id;
          // The last card asks first: with none left, the next bill fails.
          const isLast = cards.length === 1;
          return (
            <div className="ink-bill-pay" data-card="true" key={detail.id}>
              <span className="ink-bill-card">
                <span style={{ font: '400 14px/1.4 var(--ink-font)' }}>
                  <span style={{ fontWeight: 500 }}>
                    {detail.cardBrand ? `${detail.cardBrand} ` : ''}•••• {detail.cardLast4}
                  </span>
                  <span style={{ color: 'var(--ink-ink3)' }}>
                    {' '}
                    · expires {String(detail.cardExpMonth).padStart(2, '0')}/
                    {String(detail.cardExpYear).slice(-2)}
                  </span>
                  {detail.isDefault ? <span className="ink-bill-default">Default</span> : null}
                </span>
                {detail.addedBy ? (
                  <span className="ink-bill-card-by">
                    Added by {detail.addedBy}
                    {detail.addedBy === userProfileData?.me?.email ? ' (you)' : ''}
                  </span>
                ) : null}
              </span>
              {canManage && confirmingCard !== detail.id ? (
                <span style={{ display: 'flex', gap: 16 }}>
                  {!detail.isDefault ? (
                    <button
                      type="button"
                      className="ink-bill-link"
                      onClick={() => handleSetDefaultPaymentMethod(detail)}
                      disabled={Boolean(busyCard)}
                    >
                      {busy ? 'Setting…' : 'Set default'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="ink-bill-link"
                    data-muted="true"
                    onClick={() =>
                      isLast ? setConfirmingCard(detail.id) : handleRemovePaymentMethod(detail)
                    }
                    disabled={Boolean(busyCard)}
                  >
                    {busy ? 'Removing…' : 'Remove'}
                  </button>
                </span>
              ) : null}
              {confirmingCard === detail.id ? (
                <div className="ink-bill-confirm" role="alertdialog" aria-label="Remove card">
                  <span className="ink-bill-confirm-text">
                    Remove •••• {detail.cardLast4}? It’s your only card
                    {isFreePlan ? '.' : ', so your next bill fails unless you add another.'}
                  </span>
                  <button
                    type="button"
                    className="ink-quiet-link"
                    onClick={() => setConfirmingCard(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ink-btn ink-btn-danger"
                    disabled={busy}
                    onClick={() => handleRemovePaymentMethod(detail)}
                  >
                    {busy ? 'Removing…' : 'Remove card'}
                  </button>
                </div>
              ) : null}
            </div>
          );
        })
      )}
      {cardError ? (
        <p className="ink-bill-error" role="alert">
          {cardError}
        </p>
      ) : null}

      <h2 className="ink-bill-h">Invoices &amp; receipts</h2>
      <div className="ink-bill-pay" data-card="true">
        <span style={{ font: '400 13.5px/1.5 var(--ink-font)', color: 'var(--ink-ink2)' }}>
          Download past invoices and receipts.
        </span>
        {canManage ? (
          <button
            type="button"
            className="ink-btn"
            onClick={() => void goToPortal()}
            disabled={openingPortal}
          >
            {openingPortal ? 'Opening…' : 'View invoices ↗'}
          </button>
        ) : null}
      </div>
      <p className="ink-bill-foot">
        Cards, invoices and receipts are handled by Stripe. These open Stripe’s secure billing page
        in a new tab.
      </p>
      {portalError ? <p className="cp-error">{portalError}</p> : null}

      {/* Add Payment Method Modal */}
      <Dialog open={isAddPaymentMethodOpen} onOpenChange={setIsAddPaymentMethodOpen}>
        <DialogContent className="sm:max-w-md bg-core-dark">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Add Payment Method
            </DialogTitle>
            <DialogDescription>
              Add a new payment method to your account. Your payment information is secure and
              encrypted.
            </DialogDescription>
          </DialogHeader>
          {accountId && (
            <Elements stripe={stripePromise}>
              <PaymentForm
                accountId={accountId}
                userEmail={userProfileData?.me?.email}
                onSuccess={handleAddPaymentMethodSuccess}
                onCancel={() => setIsAddPaymentMethodOpen(false)}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>

      {/* Change plan: tinycms has its own (UX review v4); other products keep the dialog. */}
      {isTinycms && isChangePlanOpen ? (
        <TinycmsChangePlan
          plans={tinycmsPlans}
          currentKey={tinycmsPlan?.key || 'free'}
          currentInterval={tinycmsPlan ? orderInterval : 'year'}
          cardLast4={(cards.find((c) => c.isDefault) || cards[0])?.cardLast4 || null}
          billing={billing}
          saving={isUpdatingPlan}
          error={planError}
          onConfirm={handleSwitchTinycmsPlan}
          onAddCard={() => {
            setIsChangePlanOpen(false);
            setIsAddPaymentMethodOpen(true);
          }}
          onClose={() => setIsChangePlanOpen(false)}
        />
      ) : null}
      <Dialog open={!isTinycms && isChangePlanOpen} onOpenChange={setIsChangePlanOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-core-dark">
          <DialogHeader>
            <DialogTitle>Change Plan</DialogTitle>
            <DialogDescription>
              Select a new plan for your account. Changes will take effect immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Label className="mb-4 block">Select Your Plan</Label>
            <RadioGroup value={selectedPlan} onValueChange={(value) => setSelectedPlan(value)}>
              <div className="grid md:grid-cols-3 gap-6">
                {pricingPlans.map((plan) => (
                  <label
                    key={plan.id}
                    className={`relative cursor-pointer rounded-lg border transition-all ${
                      selectedPlan === plan.id
                        ? 'border-primary ring-2 ring-primary ring-offset-2'
                        : 'hover:border-primary/50'
                    }`}
                  >
                    <Card className="border-0">
                      <CardHeader>
                        <div className="flex items-center justify-between mb-2">
                          <RadioGroupItem value={plan.id} id={plan.id} />
                          {plan.popular && (
                            <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs">
                              Most Popular
                            </span>
                          )}
                        </div>
                        <CardTitle>{plan.name}</CardTitle>
                        <div className="mt-4">
                          <span className="font-bold">${plan.price}</span>
                          <span className="text-muted-foreground">/month</span>
                        </div>
                        <CardDescription>{plan.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setIsChangePlanOpen(false)}
              disabled={isUpdatingPlan}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSavePlan} disabled={isUpdatingPlan}>
              {isUpdatingPlan ? 'Saving...' : 'Save Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Confirmation Modal */}
      <Dialog open={isCancelSubscriptionOpen} onOpenChange={setIsCancelSubscriptionOpen}>
        <DialogContent className="sm:max-w-md bg-core-dark">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              {isComped
                ? 'Your site moves to the Free plan now. It stays online, with the “powered by tinycms.ai” bar.'
                : `You keep ${planLabel} until ${activeUntilDate || nextBillingDate}, and you won’t be charged again. Then your site moves to the Free plan — it stays online, with the “powered by tinycms.ai” bar. You can keep your plan any time before then.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => setIsCancelSubscriptionOpen(false)}
              disabled={isCancelingSubscription}
            >
              Keep Subscription
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={isCancelingSubscription}
            >
              {isCancelingSubscription ? 'Canceling...' : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Billing (title and the four plan tiles) in grey bars while it loads. */
function BillingSkeleton() {
  return (
    <div role="status" aria-label="Loading your plan" aria-busy="true">
      <h1 className="ink-h1">Billing</h1>
      <p className="ink-lede">
        Your billing details including plan, payment details, and invoices.
      </p>
      <div className="ink-bill" aria-hidden="true">
        {['Plan', 'Price', 'Seats', null].map((label, i) => (
          <div key={i}>
            <div className="ink-bill-label">
              {label ?? <span className="ink-skel-bar" style={{ display: 'block', width: 64 }} />}
            </div>
            <div className="ink-bill-value">
              <span
                className="ink-skel-bar"
                data-size="lg"
                style={{ display: 'block', width: [72, 88, 56, 96][i], marginTop: 6 }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
